// src/account.ts
import { Ed25519PublicKey } from "@mysten/sui.js/keypairs/ed25519";
import { MultiSigPublicKey } from "@mysten/sui.js/multisig";

// src/constants.ts
var AccountConfig = {
  minWeight: 0,
  maxWeight: 255,
  maxOwnerWithNonce: 9,
  maxOwnerWithoutNonce: 10,
  nonce: {
    prefix: "maven",
    weight: 1,
    maxSize: 16
  }
};
var SUI_COIN = "0x2::sui::SUI";
var UNKNOWN_COIN = "UNKNOWN COIN";

// src/account.ts
var MultiSigAccount = class _MultiSigAccount {
  constructor(config) {
    this.config = config;
    _MultiSigAccount.validate(config);
    this.publicKey = this.toMultisigPublicKey(config);
  }
  publicKey;
  get address() {
    return this.publicKey.toSuiAddress();
  }
  get publicKeys() {
    return this.publicKey.getPublicKeys();
  }
  combinePartialSignatures(signatures) {
    return this.publicKey.combinePartialSignatures(signatures);
  }
  toMultisigPublicKey(account) {
    const { ownersWithWeight, threshold, creationNonce } = account;
    const publicKeys = ownersWithWeight.map((it) => ({ ...it }));
    if (creationNonce !== void 0) {
      publicKeys.push({
        publicKey: this.makeNoncePublicKey(creationNonce),
        weight: AccountConfig.nonce.weight
      });
    }
    return MultiSigPublicKey.fromPublicKeys({ threshold, publicKeys });
  }
  makeNoncePublicKey(nonce) {
    const buffer = new ArrayBuffer(Ed25519PublicKey.SIZE);
    new TextEncoder().encodeInto(AccountConfig.nonce.prefix, new Uint8Array(buffer, 0, AccountConfig.nonce.maxSize));
    const nonceView = new DataView(buffer, AccountConfig.nonce.maxSize, 4);
    nonceView.setUint32(0, nonce, true);
    return new Ed25519PublicKey(new Uint8Array(buffer));
  }
  static validate(config) {
    config.ownersWithWeight.forEach((it) => {
      const { weight } = it;
      if (weight < AccountConfig.minWeight || weight > AccountConfig.maxWeight) {
        throw new Error(`Invalid weight: ${weight} (1-${AccountConfig.maxWeight})`);
      }
    });
    const totalWeight = config.ownersWithWeight.reduce((s, it) => s + it.weight, 0);
    if (config.threshold < 1 || config.threshold > totalWeight) {
      throw new Error(`Invalid threshold: ${config.threshold} (1-${totalWeight})`);
    }
    const maxOwner = config.creationNonce === void 0 ? AccountConfig.maxOwnerWithoutNonce : AccountConfig.maxOwnerWithNonce;
    if (config.ownersWithWeight.length > maxOwner) {
      throw new Error("Owner number bigger than upper capacity");
    }
    const addressSet = new Set(config.ownersWithWeight.map((it) => it.publicKey.toSuiAddress()));
    if (addressSet.size !== config.ownersWithWeight.length) {
      throw new Error("Duplicate address detected");
    }
  }
};

// src/crypto.ts
import {
  SIGNATURE_FLAG_TO_SCHEME as SIGNATURE_FLAG_TO_SCHEME2,
  parseSerializedSignature
} from "@mysten/sui.js/cryptography";
import { Ed25519PublicKey as Ed25519PublicKey3 } from "@mysten/sui.js/keypairs/ed25519";
import { Secp256k1PublicKey as Secp256k1PublicKey2 } from "@mysten/sui.js/keypairs/secp256k1";
import { Secp256r1PublicKey as Secp256r1PublicKey2 } from "@mysten/sui.js/keypairs/secp256r1";
import { MultiSigPublicKey as MultiSigPublicKey2 } from "@mysten/sui.js/multisig";
import { verifyPersonalMessage, verifyTransactionBlock } from "@mysten/sui.js/verify";

// src/PublicKeySerde.ts
import { SIGNATURE_FLAG_TO_SCHEME } from "@mysten/sui.js/cryptography";
import { Ed25519PublicKey as Ed25519PublicKey2 } from "@mysten/sui.js/keypairs/ed25519";
import { Secp256k1PublicKey } from "@mysten/sui.js/keypairs/secp256k1";
import { Secp256r1PublicKey } from "@mysten/sui.js/keypairs/secp256r1";
import { fromB64 } from "@mysten/sui.js/utils";
var PublicKeySerde = class {
  /**
   * Serializes a public key.
   *
   * @param {PublicKey} publicKey - The public key to serialize.
   * @return {Object} An object containing the serialized public key and the signature scheme.
   *   - publicKeyEncoded: The serialized public key encoded in base64.
   *   - scheme: The signature scheme corresponding to the public key flag.
   */
  static ser(publicKey) {
    return {
      publicKeyEncoded: publicKey.toBase64(),
      schema: SIGNATURE_FLAG_TO_SCHEME[publicKey.flag()]
    };
  }
  /**
   * Generates a public key object based on the provided input.
   *
   * @param {object} input - The input object containing the public key and the signature scheme.
   * @param {string | Uint8Array} input.publicKey - The public key.
   * @param {SignatureScheme} input.schema - The signature scheme.
   *
   * @return {PublicKey} - The generated public key object.
   *
   * @throws {Error} - If the signature scheme is unsupported.
   */
  static de(input) {
    let pkEncoded = typeof input.publicKeyEncoded === "string" ? fromB64(input.publicKeyEncoded) : input.publicKeyEncoded;
    let { schema } = input;
    if (pkEncoded.length === 34 || input.schema === "ED25519" && pkEncoded.length === 33) {
      if (pkEncoded[0] === 0 || pkEncoded[0] === 1 || pkEncoded[0] === 2) {
        schema = SIGNATURE_FLAG_TO_SCHEME[pkEncoded[0]];
        pkEncoded = pkEncoded.slice(1);
      }
    }
    switch (schema) {
      case "ED25519":
        return new Ed25519PublicKey2(pkEncoded);
      case "Secp256k1":
        return new Secp256k1PublicKey(pkEncoded);
      case "Secp256r1":
        return new Secp256r1PublicKey(pkEncoded);
      default:
        throw new Error("Unsupported signature scheme: $input.scheme");
    }
  }
};

// src/utils.ts
import { Buffer } from "buffer";
import { normalizeStructTag, normalizeSuiAddress } from "@mysten/sui.js/utils";
function isSameAddress(address1, address2) {
  return normalizeSuiAddress(address1) === normalizeSuiAddress(address2);
}
function isSuiStructEqual(struct1, struct2) {
  return normalizeStructTag(struct1) === normalizeStructTag(struct2);
}
function isCoinObjectType(struct) {
  const normalized = normalizeStructTag(struct);
  return normalized.startsWith("0x2::coin::Coin");
}
function stringToBuffer(s) {
  return Buffer.from(s, "utf-8");
}
function Uint8ArrayToHex(buffer) {
  return `0x${Array.prototype.map.call(buffer, (x) => `0${x.toString(16)}`.slice(-2)).join("")}`;
}
function HexToUint8Array(hex) {
  return Uint8Array.from(Buffer.from(hex.startsWith("0x") ? hex.slice(2) : hex, "hex"));
}

// src/crypto.ts
async function getPublicKeyFromSignature(message, type, signature) {
  switch (type) {
    case "TransactionBlock":
      return verifyTransactionBlock(message, signature);
    case "Personal":
      return verifyPersonalMessage(message, signature);
    default:
      throw new Error("Invalid message type");
  }
}
async function getPublicKeyFromPersonalSignature(message, signature) {
  return getPublicKeyFromSignature(stringToBuffer(message), "Personal", signature);
}
async function verifySignature(message, type, signature, address) {
  try {
    const publicKey = await getPublicKeyFromSignature(message, type, signature);
    return isSameAddress(publicKey.toSuiAddress(), address);
  } catch {
    return false;
  }
}
async function verifyPersonalSignature(message, signature, address) {
  return verifySignature(stringToBuffer(message), "Personal", signature, address);
}
async function verifyTransactionSignature(payload, signature, address) {
  return verifySignature(payload, "TransactionBlock", signature, address);
}
function serializePublicKey(publicKey) {
  return {
    publicKeyEncoded: publicKey.toBase64(),
    schema: SIGNATURE_FLAG_TO_SCHEME2[publicKey.flag()]
  };
}
function deserializePublicKey(publicKey, scheme) {
  switch (scheme) {
    case "ED25519":
      return new Ed25519PublicKey3(publicKey);
    case "Secp256k1":
      return new Secp256k1PublicKey2(publicKey);
    case "Secp256r1":
      return new Secp256r1PublicKey2(publicKey);
    default:
      throw new Error("Unsupported signature scheme");
  }
}
var PublicKeyManager = class {
  constructor(client) {
    this.client = client;
    this.cachedKeys = /* @__PURE__ */ new Map();
  }
  cachedKeys;
  /**
   * Get address public key from cache or from chain
   * @param address target address
   */
  async getPublicKey(address) {
    const cachedKey = this.cachedKeys.get(address);
    if (cachedKey) {
      return cachedKey;
    }
    const publicKey = await this.fetchPublicKey(address);
    if (publicKey) {
      this.cachedKeys.set(address, publicKey);
    }
    return publicKey;
  }
  /**
   * Batch get public keys from addresses
   * @param addresses addresses
   */
  async getPublicKeys(addresses) {
    const results = new Array(addresses.length).fill(void 0);
    for (let index = 0; index < addresses.length; index++) {
      const address = addresses[index];
      results[index] = this.cachedKeys.get(address);
    }
    for (let index = 0; index < results.length; index++) {
      const result = results[index];
      if (result === void 0) {
        results[index] = await this.fetchPublicKey(addresses[index]);
      }
    }
    for (let index = 0; index < addresses.length; index++) {
      const address = addresses[index];
      if (results[index]) {
        this.cachedKeys.set(address, results[index]);
      }
    }
    return results;
  }
  async fetchPublicKey(address) {
    const publicKeyFromChain = await this.fetchPublicKeyFromChain(address);
    if (publicKeyFromChain) {
      return publicKeyFromChain;
    }
    return void 0;
  }
  async fetchPublicKeyFromChain(address) {
    let txs;
    try {
      txs = await this.client.queryTransactionBlocks({
        filter: { FromAddress: address },
        options: { showInput: true },
        limit: 2
      });
    } catch {
      return void 0;
    }
    if (txs.data.length === 0 || !txs.data[0].transaction?.txSignatures) {
      return void 0;
    }
    const tx = txs.data[0];
    if (!tx.transaction) {
      return void 0;
    }
    const signatures = tx.transaction.txSignatures;
    for (let index = 0; index < signatures.length; index++) {
      const signature = signatures[index];
      const publicKey = this.getAddressFromSignature(signature, address);
      if (!publicKey) {
        continue;
      }
      return publicKey;
    }
    return void 0;
  }
  getAddressFromSignature(signature, address) {
    const parsedSignature = parseSerializedSignature(signature);
    switch (parsedSignature.signatureScheme) {
      case "MultiSig": {
        const multisigAddress = new MultiSigPublicKey2(parsedSignature.multisig.multisig_pk).toSuiAddress();
        if (isSameAddress(multisigAddress, address)) {
          throw new Error("multisig wallet cannot be owner");
        }
        return void 0;
      }
      case "ZkLogin":
        if (isSameAddress(parsedSignature.zkLogin.address, address)) {
          throw new Error("ZkLogin wallet cannot be owner");
        }
        return void 0;
      case "ED25519":
      case "Secp256k1":
      case "Secp256r1": {
        const publicKey = PublicKeySerde.de({
          publicKeyEncoded: parsedSignature.publicKey,
          schema: parsedSignature.signatureScheme
        });
        if (isSameAddress(publicKey.toSuiAddress(), address)) {
          return publicKey;
        }
        return void 0;
      }
      default:
        throw new Error("Invalid signature scheme");
    }
  }
};

// src/signature.ts
function createLoginMessage() {
  return createLoginMessageWithTimestamp((/* @__PURE__ */ new Date()).toUTCString());
}
function createLoginMessageWithTimestamp(timestamp) {
  return `Welcome to MSafe. ${timestamp}`;
}
function decodeLoginMessageTimestamp(message) {
  const regex = /Welcome to MSafe. (.+)/;
  const matches = message.match(regex);
  return matches ? matches[1] : void 0;
}
function createAccountCreationMessage(address) {
  return `Create MSafe Account: ${address}`;
}
function createProposeIntentionMessage(intention, sn) {
  return JSON.stringify({
    intention,
    sn
  });
}

// src/transaction.ts
import { TransactionBlock } from "@mysten/sui.js/transactions";
function serializeIntention(intention) {
  return JSON.stringify(intention, (_, value) => typeof value === "bigint" ? `${value.toString()}n` : value);
}
async function getAllCoins(client, owner, coinType) {
  let hasNext = true;
  let cursor;
  const res = [];
  while (hasNext) {
    const currentPage = await client.getCoins({
      owner,
      coinType,
      cursor
    });
    res.push(...currentPage.data);
    hasNext = currentPage.hasNextPage;
    cursor = currentPage.nextCursor;
  }
  return res;
}
function deserializeIntention(value) {
  const intention = JSON.parse(value);
  if (typeof intention !== "object") {
    throw new Error("Invalid intention");
  }
  return intention;
}
function buildRejectTxb(sender) {
  const block = new TransactionBlock();
  block.setSender(sender);
  return block;
}
async function buildCoinTransferTxb(client, intention, sender) {
  if (isSuiStructEqual(intention.coinType, SUI_COIN)) {
    return buildSuiCoinTransferTxb(intention, sender);
  }
  return buildOtherCoinTransferTxb(client, intention, sender);
}
function buildSuiCoinTransferTxb(intention, sender) {
  const block = new TransactionBlock();
  const [coin] = block.splitCoins(block.gas, [block.pure(intention.amount)]);
  block.transferObjects([coin], block.pure(intention.recipient));
  block.setSender(sender);
  return block;
}
async function buildOtherCoinTransferTxb(client, intention, sender) {
  const objs = await getAllCoins(client, sender, intention.coinType);
  if (objs.length === 0) {
    throw new Error("No valid coin found to send");
  }
  const totalBal = objs.reduce((sum, coin2) => sum + BigInt(coin2.balance), 0n);
  if (totalBal < BigInt(intention.amount)) {
    throw new Error("Not enough balance");
  }
  const txb = new TransactionBlock();
  const primary = txb.object(objs[0].coinObjectId);
  if (objs.length > 1) {
    txb.mergeCoins(
      primary,
      objs.slice(1).map((obj) => txb.object(obj.coinObjectId))
    );
  }
  const [coin] = txb.splitCoins(primary, [txb.pure(intention.amount)]);
  txb.transferObjects([coin], txb.pure(intention.recipient));
  txb.setSender(sender);
  return txb;
}
async function buildObjectTransferTxb(client, intention, sender) {
  await validateObjectTransfer(client, intention, sender);
  const txb = new TransactionBlock();
  txb.transferObjects([txb.object(intention.objectId)], txb.pure(intention.receiver));
  txb.setSender(sender);
  return txb;
}
async function validateObjectTransfer(client, intention, sender) {
  const obj = await client.getObject({
    id: intention.objectId,
    options: {
      showType: true,
      showOwner: true
    }
  });
  if (obj.data === void 0) {
    throw new Error("Object not found");
  }
  if (!obj.data?.type) {
    throw new Error("Object type is null");
  }
  if (!isSuiStructEqual(obj.data.type, intention.objectType)) {
    throw new Error("Object type not expected");
  }
  if (isCoinObjectType(obj.data.type)) {
    throw new Error("Can not transfer coin object in Object Transfer transactions");
  }
  const addressOwner = getAddressOwner(obj);
  if (!isSameAddress(addressOwner, sender)) {
    throw new Error("Object owner not match");
  }
}
function getAddressOwner(object) {
  const owner = object.data?.owner;
  if (!owner) {
    throw new Error("Object Owner not found");
  }
  if (typeof owner !== "object" || !("AddressOwner" in owner)) {
    throw new Error("Invalid object owner");
  }
  return owner.AddressOwner;
}

// src/stream/types.ts
var StreamStatus = /* @__PURE__ */ ((StreamStatus2) => {
  StreamStatus2["Streaming"] = "streaming";
  StreamStatus2["Completed"] = "completed";
  StreamStatus2["Canceled"] = "canceled";
  StreamStatus2["Settled"] = "settled";
  return StreamStatus2;
})(StreamStatus || {});
var StreamEventType = /* @__PURE__ */ ((StreamEventType2) => {
  StreamEventType2["Create"] = "create_stream";
  StreamEventType2["Cancel"] = "cancel_stream";
  StreamEventType2["Claim"] = "claim";
  StreamEventType2["AutoClaim"] = "auto_claim";
  StreamEventType2["SetAutoClaim"] = "set_auto_claim";
  return StreamEventType2;
})(StreamEventType || {});
var StreamTransactionType = /* @__PURE__ */ ((StreamTransactionType2) => {
  StreamTransactionType2["CREATE_STREAM"] = "CreateStream";
  StreamTransactionType2["SET_AUTO_CLAIM"] = "SetAutoClaim";
  StreamTransactionType2["CLAIM"] = "Claim";
  StreamTransactionType2["CLAIM_BY_PROXY"] = "ClaimByProxy";
  StreamTransactionType2["CANCEL"] = "Cancel";
  return StreamTransactionType2;
})(StreamTransactionType || {});

// src/stream/event.ts
var RawStreamStatusEnum = /* @__PURE__ */ ((RawStreamStatusEnum2) => {
  RawStreamStatusEnum2[RawStreamStatusEnum2["OPEN"] = 0] = "OPEN";
  RawStreamStatusEnum2[RawStreamStatusEnum2["COMPLETED"] = 1] = "COMPLETED";
  RawStreamStatusEnum2[RawStreamStatusEnum2["CANCELED"] = 16] = "CANCELED";
  RawStreamStatusEnum2[RawStreamStatusEnum2["CANCELED_COMPLETED"] = 17] = "CANCELED_COMPLETED";
  return RawStreamStatusEnum2;
})(RawStreamStatusEnum || {});

// src/stream/metadata.ts
import { bcs } from "@mysten/sui.js/bcs";
var metadataBcsType = {
  groupId: "string",
  name: "string"
};
function decodeMetadata(metaStr) {
  return bcs.de(metadataBcsType, metaStr, "base64");
}

// src/stream/helper.ts
var StreamHelper = class {
  static decodeMetadata(metaStr) {
    return decodeMetadata(metaStr);
  }
  static parseStreamStatus(rawStatus) {
    switch (rawStatus) {
      case 0:
        return "streaming" /* Streaming */;
      case 1:
        return "completed" /* Completed */;
      case 16:
        return "canceled" /* Canceled */;
      case 17:
        return "settled" /* Settled */;
      default:
        throw new Error(`Unknown Stream Status: ${rawStatus}`);
    }
  }
  static fullStreamEventType(packageId, eventType) {
    switch (eventType) {
      case "create_stream" /* Create */:
        return `${packageId}::stream::CreateEvent`;
      case "claim" /* Claim */:
        return `${packageId}::stream::ClaimEvent`;
      case "cancel_stream" /* Cancel */:
        return `${packageId}::stream::CancelEvent`;
      case "set_auto_claim" /* SetAutoClaim */:
        return `${packageId}::stream::SetAutoClaimEvent`;
      default:
        throw new Error(`Unknown StreamEventType: ${eventType}`);
    }
  }
};

// src/types.ts
var UserMSafeStatus = /* @__PURE__ */ ((UserMSafeStatus2) => {
  UserMSafeStatus2["active"] = "ACTIVE";
  UserMSafeStatus2["pending"] = "PENDING";
  UserMSafeStatus2["ignored"] = "IGNORED";
  return UserMSafeStatus2;
})(UserMSafeStatus || {});
var TransactionDefaultApplication = "msafe-core";
var TransactionType = /* @__PURE__ */ ((TransactionType2) => {
  TransactionType2["Assets"] = "Assets";
  TransactionType2["NFT"] = "NFT";
  TransactionType2["Account"] = "Account";
  TransactionType2["Swap"] = "Swap";
  TransactionType2["Staking"] = "Staking";
  TransactionType2["Bridge"] = "Bridge";
  TransactionType2["Ending"] = "Ending";
  TransactionType2["Stream"] = "Stream";
  TransactionType2["Other"] = "Other";
  return TransactionType2;
})(TransactionType || {});
var TransactionSubTypes = {
  assets: {
    coin: {
      send: "SendCoin",
      receive: "ReceiveCoin"
    },
    object: {
      send: "SendObject",
      receive: "ReceiveObject"
    }
  },
  account: {
    creation: "CreateAccount"
  },
  others: {
    plain: "PlainTransaction"
  },
  stream: {
    create: "CreateStream" /* CREATE_STREAM */,
    claim: "Claim" /* CLAIM */,
    claimByProxy: "ClaimByProxy" /* CLAIM_BY_PROXY */,
    setAutoClaim: "SetAutoClaim" /* SET_AUTO_CLAIM */,
    cancel: "Cancel" /* CANCEL */
  }
};
var OpAddressBookType = /* @__PURE__ */ ((OpAddressBookType2) => {
  OpAddressBookType2["Delete"] = "delete";
  OpAddressBookType2["Upsert"] = "upsert";
  return OpAddressBookType2;
})(OpAddressBookType || {});
var IntentionBuildStatus = /* @__PURE__ */ ((IntentionBuildStatus2) => {
  IntentionBuildStatus2["Success"] = "SUCCESS";
  IntentionBuildStatus2["Failed"] = "FAILED";
  IntentionBuildStatus2["New"] = "NEW";
  return IntentionBuildStatus2;
})(IntentionBuildStatus || {});

// src/transactionResponseDecoder.ts
import { normalizeSuiAddress as normalizeSuiAddress2, normalizeSuiObjectId, normalizeStructTag as normalizeStructTag3 } from "@mysten/sui.js/utils";

// src/coin.ts
import { normalizeStructTag as normalizeStructTag2 } from "@mysten/sui.js/utils";
var CoinHelper = class {
  _client;
  _queryCache;
  _coinMetaReg;
  constructor(client) {
    this._client = client;
    this._queryCache = /* @__PURE__ */ new Map();
    this._coinMetaReg = /* @__PURE__ */ new Map();
  }
  async getCoinMeta(coinType) {
    const normalized = normalizeStructTag2(coinType);
    if (this._coinMetaReg.has(normalized)) {
      return this._coinMetaReg.get(normalized);
    }
    if (!this._queryCache.has(normalized)) {
      this._queryCache.set(
        normalized,
        this.queryCoinMeta(normalized).then((meta) => {
          if (meta) {
            this._coinMetaReg.set(normalized, meta);
          }
          return meta;
        })
      );
    }
    return this._queryCache.get(normalized);
  }
  async queryCoinMeta(coinType) {
    const res = await this._client.getCoinMetadata({ coinType });
    return res || void 0;
  }
};
var COIN_TYPE_ARG_REGEX = /^0x0000000000000000000000000000000000000000000000000000000000000002::coin::Coin<(.+)>$/;
var Coin = class _Coin {
  static isCoin(type) {
    if (!type) {
      return false;
    }
    return normalizeStructTag2(type).match(COIN_TYPE_ARG_REGEX) != null;
  }
  static getCoinType(type) {
    const [, res] = normalizeStructTag2(type).match(COIN_TYPE_ARG_REGEX) ?? [];
    return res || null;
  }
  static getBalance(data) {
    if (!_Coin.isCoin(data.type)) {
      return void 0;
    }
    if (data.content?.dataType !== "moveObject") {
      return void 0;
    }
    const { balance } = data.content?.fields;
    if (balance === void 0) {
      return void 0;
    }
    return BigInt(balance);
  }
};

// src/transactionResponseDecoder.ts
var TransactionResponseDataDecoder = class _TransactionResponseDataDecoder {
  constructor(transactionData) {
    this.transactionData = transactionData;
  }
  // coin transfer composed of [MergeCoins], SplitCoins and TransferObjects
  isCoinTransfer() {
    const transactions = this.transactionData.transaction.kind === "ProgrammableTransaction" ? this.transactionData.transaction.transactions : null;
    if (!transactions || transactions.length < 2 || transactions.length > 3) {
      return false;
    }
    if (transactions.length === 2) {
      return "SplitCoins" in transactions[0] && "TransferObjects" in transactions[1];
    }
    return "SplitCoins" in transactions[1] && "TransferObjects" in transactions[2];
  }
  isObjectTransfer() {
    if (this.transactionData.transaction.kind !== "ProgrammableTransaction") {
      return false;
    }
    const { transactions } = this.transactionData.transaction;
    if (transactions.length !== 1) {
      return false;
    }
    return "TransferObjects" in transactions[0] && transactions[0].TransferObjects != null;
  }
  async coinTransferInfo(suiClient) {
    if (!this.isCoinTransfer()) {
      throw new Error("Not a coin transfer");
    }
    if (this.transactionData.transaction.kind !== "ProgrammableTransaction") {
      throw new Error("Not a coin transfer");
    }
    const { transactions } = this.transactionData.transaction;
    const { inputs } = this.transactionData.transaction;
    const splitPayload = transactions.length === 2 ? transactions[0] : transactions[1];
    const transferPayload = transactions.length === 2 ? transactions[1] : transactions[2];
    if (!("SplitCoins" in splitPayload && "TransferObjects" in transferPayload)) {
      throw new Error("Not a coin transfer");
    }
    const splitAmount = _TransactionResponseDataDecoder.decodePureInput(inputs, {
      index: splitPayload.SplitCoins[1][0].Input
    });
    const toAddress = _TransactionResponseDataDecoder.decodePureInput(inputs, {
      index: transferPayload.TransferObjects[1].Input
    });
    let coinType = normalizeStructTag3(SUI_COIN);
    if (splitPayload.SplitCoins[0] !== "GasCoin") {
      const objectDecoded = _TransactionResponseDataDecoder.decodeObjectInput(inputs, {
        index: splitPayload.SplitCoins[0].Input
      });
      const objectInfo = await suiClient.tryGetPastObject({
        id: objectDecoded.objectId,
        version: objectDecoded.version,
        options: { showType: true }
      });
      if (objectInfo.status === "VersionFound") {
        const foundCoinType = objectInfo.details.type;
        const suiCoinType = Coin.getCoinType(foundCoinType);
        coinType = suiCoinType ? normalizeStructTag3(suiCoinType) : UNKNOWN_COIN;
      }
    }
    return {
      coinType,
      amount: BigInt(splitAmount),
      to: normalizeSuiAddress2(toAddress)
    };
  }
  async objectTransferInfo(suiClient) {
    if (!this.isObjectTransfer()) {
      throw new Error("Not an object transfer");
    }
    if (this.transactionData.transaction.kind !== "ProgrammableTransaction") {
      throw new Error("Not an object transfer");
    }
    const { transactions, inputs } = this.transactionData.transaction;
    const transactionPayload = transactions.pop();
    const transferObjects = "TransferObjects" in transactionPayload && transactionPayload.TransferObjects;
    const toAddress = _TransactionResponseDataDecoder.decodePureInput(inputs, {
      index: transferObjects[1].Input
    });
    const transferObjectsInput = transferObjects[0][0].Input;
    if (transferObjectsInput >= 0) {
      const objectDecoded = _TransactionResponseDataDecoder.decodeObjectInput(inputs, {
        index: transferObjectsInput
      });
      const objectInfo = await suiClient.tryGetPastObject({
        id: objectDecoded.objectId,
        version: objectDecoded.version,
        options: { showType: true }
      });
      return {
        objectId: normalizeSuiObjectId(objectDecoded.objectId),
        objectVersion: objectDecoded.version.toString(),
        objectType: objectInfo.status === "VersionFound" ? objectInfo.details.type : "",
        to: normalizeSuiAddress2(toAddress)
      };
    }
    if (transferObjects[0][0] === "GasCoin") {
      return {
        objectId: normalizeSuiObjectId("0x2"),
        objectVersion: "0",
        objectType: normalizeSuiObjectId("0x2"),
        to: normalizeSuiAddress2(toAddress)
      };
    }
    throw new Error("invliad object transfer tx");
  }
  static decodeObjectInput(inputs, param) {
    const input = inputs[param.index];
    return {
      objectId: normalizeSuiObjectId(input.objectId),
      version: parseInt(input.version ?? input.initialSharedVersion, 10)
    };
  }
  static decodePureInput(inputs, param) {
    const input = inputs[param.index];
    return input.value;
  }
};

// src/api/sync.ts
var API_SIGNATURE_KEY_HEADER = "Authorization-Signature";

// src/api/transaction.ts
var TransactionStatus = /* @__PURE__ */ ((TransactionStatus2) => {
  TransactionStatus2["SUCCESS"] = "SUCCESS";
  TransactionStatus2["FAILED"] = "FAILED";
  TransactionStatus2["REJECTED"] = "REJECTED";
  return TransactionStatus2;
})(TransactionStatus || {});
var TransactionIntentionStatus = /* @__PURE__ */ ((TransactionIntentionStatus2) => {
  TransactionIntentionStatus2["NEW"] = "NEW";
  TransactionIntentionStatus2["SUCCESS"] = "SUCCESS";
  TransactionIntentionStatus2["FAILED"] = "FAILED";
  return TransactionIntentionStatus2;
})(TransactionIntentionStatus || {});

// src/signingMessageHelper.ts
import { normalizeSuiAddress as normalizeSuiAddress3 } from "@mysten/sui.js/utils";
import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex } from "@noble/hashes/utils";
import sortKeys from "sort-keys-recursive";
var SigningMessageHelper = class _SigningMessageHelper {
  static createMSafeMessage(msafeAddress) {
    return `Create MSafe Account: ${msafeAddress}`;
  }
  static loginMessageWithTimestamp(timestamp) {
    return `Welcome to MSafe. ${timestamp}`;
  }
  static loginMessage() {
    return _SigningMessageHelper.loginMessageWithTimestamp((/* @__PURE__ */ new Date()).toUTCString());
  }
  static decodeLoginMessageTimestamp(message) {
    const regex = /Welcome to MSafe. (.+)/;
    const matches = message.match(regex);
    return matches ? matches[1] : void 0;
  }
  static proposeIntentionMessage(input) {
    const sortedInput = sortKeys(input);
    const message = JSON.stringify(
      sortedInput,
      (_, value) => typeof value === "bigint" ? `${value.toString()}n` : value
    );
    const hash = sha256(message);
    const hashStr = bytesToHex(hash);
    return `Submit a new transaction proposal [${input.sn}]: ${hashStr}`;
  }
  static updateAddressBookMessage(updates) {
    const sortedUpdates = updates.map((update) => ({
      ...update,
      address: normalizeSuiAddress3(update.address)
    })).map((update) => _SigningMessageHelper.sortObjectKeys(update));
    const raw = JSON.stringify(
      sortedUpdates,
      (_, value) => typeof value === "bigint" ? `${value.toString()}n` : value,
      2
    );
    const encoded = stringToBuffer(raw);
    if (encoded.length <= 1e3) {
      return `Address book entry update: ${raw}`;
    }
    return `Bulk address book update: ${sha256(raw)}`;
  }
  static sortObjectKeys(obj) {
    return Object.keys(obj).sort().reduce((result, key) => ({ ...result, [key]: obj[key] }), {});
  }
};

// src/sync.ts
import { sha256 as sha2562 } from "@noble/hashes/sha256";
import { bytesToHex as bytesToHex2 } from "@noble/hashes/utils";
import sortKeys2 from "sort-keys-recursive";
var SyncField = /* @__PURE__ */ ((SyncField2) => {
  SyncField2["checkpoint"] = "syncCheckpoint";
  return SyncField2;
})(SyncField || {});
var SyncApiService = class {
  signPayload(input) {
    const { payload, secretKey } = input;
    const sortedPayload = sortKeys2(payload);
    const signingMessage = `${JSON.stringify(sortedPayload, (_, value) => typeof value === "bigint" ? `${value.toString()}n` : value)}::${secretKey}`;
    const hash = sha2562(signingMessage);
    const signature = bytesToHex2(hash);
    return {
      payload,
      signature
    };
  }
  verify(input) {
    const { payload, secretKey, signature } = input;
    const result = this.signPayload({ payload, secretKey });
    return result.signature === signature;
  }
};

// src/jira.ts
var IssueType = /* @__PURE__ */ ((IssueType2) => {
  IssueType2["Bug"] = "Bug";
  IssueType2["Feature"] = "Feature";
  IssueType2["Support"] = "Support";
  IssueType2["Auto"] = "Auto";
  return IssueType2;
})(IssueType || {});
var ContactType = /* @__PURE__ */ ((ContactType2) => {
  ContactType2["Email"] = "Email";
  ContactType2["Telegram"] = "Telegram";
  ContactType2["Discord"] = "Discord";
  ContactType2["Phone"] = "Phone";
  return ContactType2;
})(ContactType || {});

// src/address-book.ts
var AddressBookMode = /* @__PURE__ */ ((AddressBookMode2) => {
  AddressBookMode2["Unknown"] = "unknown";
  AddressBookMode2["Local"] = "local";
  AddressBookMode2["Remote"] = "remote";
  return AddressBookMode2;
})(AddressBookMode || {});
var ADDRESS_BOOK_SETTING_KEY = "address-book-mode";
export {
  ADDRESS_BOOK_SETTING_KEY,
  API_SIGNATURE_KEY_HEADER,
  AccountConfig,
  AddressBookMode,
  COIN_TYPE_ARG_REGEX,
  Coin,
  CoinHelper,
  ContactType,
  HexToUint8Array,
  IntentionBuildStatus,
  IssueType,
  MultiSigAccount,
  OpAddressBookType,
  PublicKeyManager,
  PublicKeySerde,
  RawStreamStatusEnum,
  SUI_COIN,
  SigningMessageHelper,
  StreamEventType,
  StreamHelper,
  StreamStatus,
  StreamTransactionType,
  SyncApiService,
  SyncField,
  TransactionDefaultApplication,
  TransactionIntentionStatus,
  TransactionResponseDataDecoder,
  TransactionStatus,
  TransactionSubTypes,
  TransactionType,
  UNKNOWN_COIN,
  Uint8ArrayToHex,
  UserMSafeStatus,
  buildCoinTransferTxb,
  buildObjectTransferTxb,
  buildOtherCoinTransferTxb,
  buildRejectTxb,
  buildSuiCoinTransferTxb,
  createAccountCreationMessage,
  createLoginMessage,
  createLoginMessageWithTimestamp,
  createProposeIntentionMessage,
  decodeLoginMessageTimestamp,
  deserializeIntention,
  deserializePublicKey,
  getAllCoins,
  getPublicKeyFromPersonalSignature,
  getPublicKeyFromSignature,
  isCoinObjectType,
  isSameAddress,
  isSuiStructEqual,
  serializeIntention,
  serializePublicKey,
  stringToBuffer,
  verifyPersonalSignature,
  verifySignature,
  verifyTransactionSignature
};
//# sourceMappingURL=index.mjs.map